import React, {useRef} from "react";
import {css} from "@panda/css";

import {CloseIcon, SearchIcon} from "@pg-design/icons-module";
import {NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";

import {SearchAutocomplete, type SearchAutocompleteController} from "../../../search/components/search_autocomplete/SearchAutocomplete";
import {SearchAutocompleteDropdownPortal} from "../../../search/components/search_autocomplete/SearchAutocompleteDropdownPortal";
import {useSearchAutocompleteInput} from "../../../search/components/search_autocomplete/useSearchAutocompleteInput";
import {HomepageSearchDropdownList} from "./HomepageSearchDropdownList";
import {IHomepageSearchViewProps} from "./HomepageSearchTypes";

export const HomepageHeroDesktopSearchInput = (props: IHomepageSearchViewProps) => {
    return (
        <div className={heroSearchAutocompleteWrapperStyle}>
            <SearchAutocomplete
                values={props.formValues}
                onChange={props.onChange}
                onAfterChange={props.onAfterChange}
                onSubmit={props.autocompleteSubmit}
                onDropdownStatusChange={props.setDesktopSearchBlendActive}
                onAfterTabSwitch={props.onAfterTabSwitch}
                fetchAllSearchLists={props.fetchAllSearchLists}
                autoFocus={props.autoFocus ?? true}
                inputIcon={<SearchIcon size="2.4" />}
                isHomepage={false}
                hideArrowIcon
                disableMultiRegion={!!props.disableMultiRegion}
                minWidth="auto"
                useDropdownPortal
                renderDropdownList={HomepageSearchDropdownList}
                renderLayout={(controller) => <HomepageHeroDesktopSearchInputLayout controller={controller} />}
            />
        </div>
    );
};

const HomepageHeroDesktopSearchInputLayout = ({controller}: {controller: SearchAutocompleteController}) => {
    const input = useSearchAutocompleteInput(controller.inputProps);
    const anchorRef = useRef<HTMLDivElement>(null);

    return (
        <>
            <div ref={anchorRef} className={heroInputFrameStyle} onClick={input.onDivClick}>
                <div className={heroInputIconStyle}>{controller.inputProps.inputIcon ?? <SearchIcon size="2.4" />}</div>
                {controller.inputProps.regionPillLabels.length > 0 && !controller.inputProps.isTravelTimeTabActive && (
                    <div className={selectedRegionPillsWrapperStyle}>
                        {controller.inputProps.regionPillLabels.map((regionPillLabel, index) => {
                            const shouldShowCloseIcon = index === controller.inputProps.regionPillLabels.length - 1;

                            if (shouldShowCloseIcon) {
                                return (
                                    <button
                                        key={`${regionPillLabel}-${index.toString()}`}
                                        type="button"
                                        className={selectedRegionPillStyle}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            controller.inputProps.onRemoveAllRegions();
                                        }}
                                    >
                                        <span className={selectedRegionPillTextStyle}>{regionPillLabel}</span>
                                        <CloseIcon size="1.2" wrapperSize="1.4" wrapperColor="transparent" />
                                    </button>
                                );
                            }

                            return (
                                <span key={`${regionPillLabel}-${index.toString()}`} className={selectedRegionPillStyle}>
                                    <span className={selectedRegionPillTextStyle}>{regionPillLabel}</span>
                                </span>
                            );
                        })}
                    </div>
                )}
                <input
                    ref={input.inputRef}
                    data-testid={NAVIGATION_TEST_IDS.SEARCH_BAR.INPUT}
                    autoComplete="off"
                    name={controller.inputProps.name}
                    value={input.inputValue}
                    placeholder={controller.inputProps.placeholder}
                    onChange={input.onChange}
                    onMouseUp={input.onMouseUp}
                    readOnly={controller.inputProps.readOnly}
                    autoFocus={controller.inputProps.autoFocus}
                    disabled={controller.inputProps.inputDisabled}
                    className={heroInputStyle}
                />
            </div>
            <SearchAutocompleteDropdownPortal anchorRef={anchorRef} portalRef={controller.dropdownPortalRef} dropdownProps={controller.dropdownProps}>
                <div className={heroDropdownContentStyle}>{controller.renderDropdownList()}</div>
            </SearchAutocompleteDropdownPortal>
        </>
    );
};

const heroSearchAutocompleteWrapperStyle = css({
    position: "relative",
    width: "100%"
});

const heroInputFrameStyle = css({
    position: "relative",
    zIndex: 1001,
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: "72px",
    gap: 1,
    boxSizing: "border-box",
    pl: 3,
    pr: 2,
    border: 0,
    borderRadius: "999px",
    backgroundColor: "#fff",
    overflow: "hidden",
    cursor: "text"
});

const heroInputIconStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
    width: "2.4rem",
    height: "100%",
    color: "var(--colors-gray-900)"
});

const selectedRegionPillsWrapperStyle = css({
    display: "flex",
    alignItems: "center",
    flex: "0 1 auto",
    minWidth: 0,
    gap: 0.5
});

const selectedRegionPillStyle = css({
    display: "flex",
    alignItems: "center",
    flex: "0 0 auto",
    maxWidth: "18rem",
    height: "32px",
    minHeight: "32px",
    gap: 0.5,
    px: 1,
    py: 0,
    border: 0,
    borderRadius: "999px",
    backgroundColor: "var(--colors-primary)",
    color: "var(--colors-gray-900)",
    cursor: "pointer"
});

const selectedRegionPillTextStyle = css({
    maxWidth: "12rem",
    overflow: "hidden",
    color: "var(--colors-gray-900)",
    fontSize: "1.2rem",
    lineHeight: "1.6rem",
    fontWeight: 500,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis"
});

const heroInputStyle = css({
    flex: "1 1 auto",
    minWidth: 0,
    height: "100%",
    p: 0,
    border: 0,
    outline: "none",
    backgroundColor: "transparent",
    color: "var(--colors-gray-900)",
    fontSize: "1.6rem",
    lineHeight: "2.4rem",

    _placeholder: {
        color: "var(--colors-gray-900)",
        opacity: 1
    }
});

const heroDropdownContentStyle = css({
    p: "2 1"
});
import React, {useRef} from "react";
import {css} from "@panda/css";

import {CloseIcon, SearchIcon} from "@pg-design/icons-module";
import {NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";

import {SearchAutocomplete, type SearchAutocompleteController} from "../../../search/components/search_autocomplete/SearchAutocomplete";
import {SearchAutocompleteDropdownPortal} from "../../../search/components/search_autocomplete/SearchAutocompleteDropdownPortal";
import {useSearchAutocompleteInput} from "../../../search/components/search_autocomplete/useSearchAutocompleteInput";
import {HomepageSearchDropdownList} from "./HomepageSearchDropdownList";
import {IHomepageSearchViewProps} from "./HomepageSearchTypes";

export const HomepageHeroDesktopSearchInput = (props: IHomepageSearchViewProps) => {
    return (
        <div className={heroSearchAutocompleteWrapperStyle}>
            <SearchAutocomplete
                values={props.formValues}
                onChange={props.onChange}
                onAfterChange={props.onAfterChange}
                onSubmit={props.autocompleteSubmit}
                onDropdownStatusChange={props.setDesktopSearchBlendActive}
                onAfterTabSwitch={props.onAfterTabSwitch}
                fetchAllSearchLists={props.fetchAllSearchLists}
                autoFocus={props.autoFocus ?? true}
                inputIcon={<SearchIcon size="2.4" />}
                isHomepage={false}
                hideArrowIcon
                disableMultiRegion={!!props.disableMultiRegion}
                minWidth="auto"
                useDropdownPortal
                renderDropdownList={HomepageSearchDropdownList}
                renderLayout={(controller) => <HomepageHeroDesktopSearchInputLayout controller={controller} />}
            />
        </div>
    );
};

const HomepageHeroDesktopSearchInputLayout = ({controller}: {controller: SearchAutocompleteController}) => {
    const input = useSearchAutocompleteInput(controller.inputProps);
    const anchorRef = useRef<HTMLDivElement>(null);

    return (
        <>
            <div ref={anchorRef} className={heroInputFrameStyle} onClick={input.onDivClick}>
                <div className={heroInputIconStyle}>{controller.inputProps.inputIcon ?? <SearchIcon size="2.4" />}</div>
                {controller.inputProps.regionPillLabels.length > 0 && !controller.inputProps.isTravelTimeTabActive && (
                    <div className={selectedRegionPillsWrapperStyle}>
                        {controller.inputProps.regionPillLabels.map((regionPillLabel, index) => {
                            const shouldShowCloseIcon = index === controller.inputProps.regionPillLabels.length - 1;

                            if (shouldShowCloseIcon) {
                                return (
                                    <button
                                        key={`${regionPillLabel}-${index.toString()}`}
                                        type="button"
                                        className={selectedRegionPillStyle}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            controller.inputProps.onRemoveAllRegions();
                                        }}
                                    >
                                        <span className={selectedRegionPillTextStyle}>{regionPillLabel}</span>
                                        <CloseIcon size="1.2" wrapperSize="1.4" wrapperColor="transparent" />
                                    </button>
                                );
                            }

                            return (
                                <span key={`${regionPillLabel}-${index.toString()}`} className={selectedRegionPillStyle}>
                                    <span className={selectedRegionPillTextStyle}>{regionPillLabel}</span>
                                </span>
                            );
                        })}
                    </div>
                )}
                <input
                    ref={input.inputRef}
                    data-testid={NAVIGATION_TEST_IDS.SEARCH_BAR.INPUT}
                    autoComplete="off"
                    name={controller.inputProps.name}
                    value={input.inputValue}
                    placeholder={controller.inputProps.placeholder}
                    onChange={input.onChange}
                    onMouseUp={input.onMouseUp}
                    readOnly={controller.inputProps.readOnly}
                    autoFocus={controller.inputProps.autoFocus}
                    disabled={controller.inputProps.inputDisabled}
                    className={heroInputStyle}
                />
            </div>
            <SearchAutocompleteDropdownPortal anchorRef={anchorRef} portalRef={controller.dropdownPortalRef} dropdownProps={controller.dropdownProps}>
                <div className={heroDropdownContentStyle}>{controller.renderDropdownList()}</div>
            </SearchAutocompleteDropdownPortal>
        </>
    );
};

const heroSearchAutocompleteWrapperStyle = css({
    position: "relative",
    width: "100%"
});

const heroInputFrameStyle = css({
    position: "relative",
    zIndex: 1001,
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: "72px",
    gap: 1,
    boxSizing: "border-box",
    pl: 3,
    pr: 2,
    border: 0,
    borderRadius: "999px",
    backgroundColor: "#fff",
    overflow: "hidden",
    cursor: "text"
});

const heroInputIconStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
    width: "2.4rem",
    height: "100%",
    color: "var(--colors-gray-900)"
});

const selectedRegionPillsWrapperStyle = css({
    display: "flex",
    alignItems: "center",
    flex: "0 1 auto",
    minWidth: 0,
    gap: 0.5
});

const selectedRegionPillStyle = css({
    display: "flex",
    alignItems: "center",
    flex: "0 0 auto",
    maxWidth: "18rem",
    height: "32px",
    minHeight: "32px",
    gap: 0.5,
    px: 1,
    py: 0,
    border: 0,
    borderRadius: "999px",
    backgroundColor: "var(--colors-primary)",
    color: "var(--colors-gray-900)",
    cursor: "pointer"
});

const selectedRegionPillTextStyle = css({
    maxWidth: "12rem",
    overflow: "hidden",
    color: "var(--colors-gray-900)",
    fontSize: "1.2rem",
    lineHeight: "1.6rem",
    fontWeight: 500,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis"
});

const heroInputStyle = css({
    flex: "1 1 auto",
    minWidth: 0,
    height: "100%",
    p: 0,
    border: 0,
    outline: "none",
    backgroundColor: "transparent",
    color: "var(--colors-gray-900)",
    fontSize: "1.6rem",
    lineHeight: "2.4rem",

    _placeholder: {
        color: "var(--colors-gray-900)",
        opacity: 1
    }
});

const heroDropdownContentStyle = css({
    p: "2 1"
});
